import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Button,
  Chip,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Tooltip,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import axios from "axios";
import Images from "../../../utils/Images";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function DamagedProducts() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));

  // ✅ define platform
  const platform = "WEB";

  // data
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // selection
  const [selectedProduct, setSelectedProduct] = useState(null);

  // header
  const [date, setDate] = useState(todayYYYYMMDD());

  // current item config
  const [qty, setQty] = useState(1);
  const [addonSelections, setAddonSelections] = useState({});
  const [remark, setRemark] = useState("");

  // submit state
  const [posting, setPosting] = useState(false);

  const flattenCategoriesToProducts = (categoryList) => {
    if (!Array.isArray(categoryList)) return [];
    return categoryList.flatMap((cat) =>
      (cat.products || []).map((prod) => ({
        ...prod,
        category: { _id: cat._id, name: cat.name, description: cat.description },
      }))
    );
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) return;

    try {
      setLoadingProducts(true);
      const response = await axios.get(`${API.BASE_URL}product/getProductsWithAddons`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { outletId: outletData._id, orderType: "PICKUP" },
      });

      const fetchedCategories = response?.data?.data?.categories || [];
      setCategories(fetchedCategories);

      const flattened = flattenCategoriesToProducts(fetchedCategories);
      setAllProducts(flattened);
    } catch (e) {
      console.error("Error fetching products with addons:", e);
      toast.error("Failed to fetch products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    getCategoriesProductsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const products = useMemo(() => {
    const search = (searchQuery || "").toLowerCase().trim();
    if (!search) return [];

    return allProducts.filter((p) => {
      const categoryOk = selectedCategoryId ? p?.category?._id === selectedCategoryId : true;
      if (!categoryOk) return false;

      const nameMatch = p.name?.toLowerCase().includes(search);
      const descMatch = p.description?.toLowerCase().includes(search);
      const keywordsMatch = p.keywords?.toLowerCase().includes(search);

      return nameMatch || descMatch || keywordsMatch;
    });
  }, [allProducts, searchQuery, selectedCategoryId]);

  useEffect(() => {
    setAddonSelections({});
    setQty(1);
    setRemark("");
  }, [selectedProduct]);

  const getValidAddonValues = (addon) => {
    if (!addon?.values?.length) return [];
    return addon.values.filter((v) => {
      const isActiveOk = v?.status !== "INACTIVE" || v?.isDefault;
      const notExcluded = !v?.excludedOutlets?.includes(outletData?._id) || v?.isDefault;
      const notIconInfo = !v?.iconInfo;
      return isActiveOk && notExcluded && notIconInfo;
    });
  };

  const selectAddonValue = (addon, value) => {
    setAddonSelections((prev) => ({
      ...prev,
      [addon._id]: {
        addonId: addon._id,
        addonValueId: value._id,
        addonTitle: addon.title,
        addonValueTitle: value.value,
      },
    }));
  };

  const validateCurrentItem = () => {
    if (!selectedProduct?._id) return "Please select a product.";
    if (selectedProduct?.isExhausted) return "Product is exhausted.";
    if (num(qty) <= 0) return "Quantity must be greater than 0.";

    const r = (remark || "").trim();
    if (!r) return "Reason for damage is required.";
    if (r.length < 50) return "Reason must be at least 50 characters.";

    if (selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons)) {
      const requiredAddons = selectedProduct.addons
        .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
        .filter((a) => a?.isOptional === false);

      for (const addon of requiredAddons) {
        const validValues = getValidAddonValues(addon);
        if (!validValues.length) continue;
        if (!addonSelections[addon._id]) return `Select required addon: ${addon.title}`;
      }
    }

    return null;
  };

  const submitDamaged = async () => {
    const err = validateCurrentItem();
    if (err) return toast.error(err);

    const toastId = toast.loading("Posting damaged product...");
    try {
      setPosting(true);

      const addonsArray = Object.values(addonSelections);

      const body = {
        date,
        product: selectedProduct._id,
        outlet: outletData?._id,
        addons: addonsArray,
        quantity: num(qty),
        remark: (remark || "").trim(),
      };

      await axios.post(`${API.BASE_URL}damaged-product-entry`, body, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      // reset
      setSelectedProduct(null);
      setSearchQuery("");
      setSelectedCategoryId(null);
      setAddonSelections({});
      setQty(1);
      setRemark("");
      navigate('/damaged-report');
      toast.update(toastId, {
        render: "Damaged product submitted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Posting failed";
      toast.update(toastId, {
        render: msg,
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setPosting(false);
    }
  };

  const submitDisabled =
    posting ||
    !selectedProduct?._id ||
    num(qty) <= 0 ||
    (remark || "").trim().length < 50 ||
    selectedProduct?.isExhausted;

  return (
    <Box sx={{ height: "100vh", backgroundColor: "#f5f6f8" }}>
      <Grid container sx={{ height: "100%" }}>
        <Grid item md={7} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: "1px solid #eee", background: "#fff" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>Products</Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "#fafafa", borderRadius: 2 }}
              />
            </Box>

            {/* Body */}
            <Box sx={{ p: 2, height: "calc(100% - 140px)", overflow: "auto" }}>
              {loadingProducts ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress sx={{ color: "#111" }} />
                  <Typography sx={{ mt: 1, opacity: 0.7 }}>Fetching products...</Typography>
                </Box>
              ) : !searchQuery.trim() ? (
                <Typography sx={{ textAlign: "center", py: 6, opacity: 0.7 }}>
                  Start typing in search to view products.
                </Typography>
              ) : products.length ? (
                <Grid container spacing={2} justifyContent={"center"}>
                  {products.map((product) => {
                    const active = selectedProduct?._id === product._id;
                    return (
                      <Grid item xs={12} sm={6} md={6} lg={4} key={product._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            borderColor: active ? "#00A4E2" : "#eee",
                            boxShadow: active ? "0 0 0 2px rgba(0,164,226,0.25)" : "none",
                            backgroundColor: product.isExhausted ? "#FFF4F4" : "#fff",
                          }}
                        >
                          <CardActionArea onClick={() => setSelectedProduct(product)}>
                            {/* ✅ More padding */}
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
                                <Avatar
                                  variant="rounded"
                                  src={product.displayImageUrl || product.displayImage}
                                  alt={product.name}
                                  imgProps={{
                                    onError: (e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = Images.product;
                                    },
                                  }}
                                  // ✅ Bigger avatar
                                  sx={{ width: 64, height: 64, borderRadius: 2 }}
                                />

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  {/* ✅ Product name visible on small screens (2-line clamp instead of ellipsis) */}
                                  <Typography
                                    sx={{
                                      fontSize: 15,
                                      fontWeight: 900,
                                      lineHeight: 1.25,
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {product.name}
                                  </Typography>

                                  <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 0.2 }}>
                                    {product.category?.name || "-"}
                                  </Typography>
                                </Box>

                                {product.isExhausted ? (
                                  <Tooltip title="Exhausted">
                                    <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography sx={{ textAlign: "center", py: 6, opacity: 0.7 }}>
                  No products found for “{searchQuery}”.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item md={5} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                px: 2,
                pt: 2,
                pb: 1.5,
                borderBottom: "1px solid #eee",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 900, lineHeight: 1.1 }}>
                    Damaged / Wastage Products
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                    {outletData?.name || "Outlet"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  size="small"
                  fullWidth
                  label="Quantity"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ px: 2, py: 2, overflow: "auto", flex: 1 }}>
              {!selectedProduct ? (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography sx={{ fontSize: 13, opacity: 0.75, mb: 1 }}>
                    Select a product to start
                  </Typography>
                  <img
                    src={Images.noProductSelected}
                    alt="product-not-selected"
                    style={{ maxWidth: 220, opacity: 0.9 }}
                  />
                </Box>
              ) : (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: "#e9eef3",
                    background: "linear-gradient(180deg, #ffffff 0%, #fafcff 100%)",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 1.5, display: "flex", gap: 1.2, alignItems: "center" }}>
                    <Avatar
                      variant="rounded"
                      src={selectedProduct.displayImageUrl || selectedProduct.displayImage}
                      alt={selectedProduct.name}
                      imgProps={{
                        onError: (e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = Images.product;
                        },
                      }}
                      sx={{ width: 54, height: 54, borderRadius: 2 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 900 }} noWrap>
                        {selectedProduct.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                        {selectedProduct.category?.name || "-"}
                      </Typography>
                    </Box>

                    {selectedProduct.isExhausted ? (
                      <Tooltip title="Exhausted">
                        <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label="Selected"
                        sx={{
                          fontWeight: 800,
                          bgcolor: "rgba(0,164,226,0.12)",
                          color: "#006B96",
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    {selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons) ? (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 900, mb: 1 }}>Addons</Typography>

                        <Stack spacing={1}>
                          {selectedProduct?.addons
                            .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
                            .map((addon) => {
                              const values = (addon?.values || []).filter((v) => {
                                const isActiveOk = v?.status !== "INACTIVE" || v?.isDefault;
                                const notExcluded =
                                  !v?.excludedOutlets?.includes(outletData?._id) || v?.isDefault;
                                const notIconInfo = !v?.iconInfo;
                                return isActiveOk && notExcluded && notIconInfo;
                              });

                              if (!values.length) return null;

                              const selected = addonSelections?.[addon._id]?.addonValueId;

                              return (
                                <Box
                                  key={addon?._id}
                                  sx={{
                                    p: 1.2,
                                    borderRadius: 2,
                                    border: "1px solid #eef2f6",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                                      {addon?.title}
                                    </Typography>
                                    {!addon?.isOptional && (
                                      <Chip size="small" label="Required" sx={{ height: 20, fontSize: 11 }} />
                                    )}
                                  </Box>

                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {values?.map((v) => {
                                      const active = selected === v?._id;
                                      return (
                                        <Chip
                                          key={v?._id}
                                          label={`${v?.subLabel || v?.value}`}
                                          onClick={() => selectAddonValue(addon, v)}
                                          variant={active ? "filled" : "outlined"}
                                          sx={{
                                            borderRadius: 999,
                                            ...(active
                                              ? { backgroundColor: "#00A4E2", color: "#fff" }
                                              : { borderColor: "#00A4E2", color: "#00A4E2" }),
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              );
                            })}
                        </Stack>
                      </Box>
                    ) : null}

                    {/* Reason */}
                    <Card
                      elevation={0}
                      sx={{
                        mt: 2,
                        borderRadius: 3,
                        border: "1px solid #e6eaf0",
                        background: "#fff",
                      }}
                    >
                      <CardContent>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                          Reason for Damage <span style={{ opacity: 0.7 }}>(min 50 chars)</span>
                        </Typography>

                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Enter reason (required)"
                          value={remark}
                          onChange={(e) => setRemark(e.target.value)}
                          helperText={`${(remark || "").trim().length}/50`}
                          error={(remark || "").trim().length > 0 && (remark || "").trim().length < 50}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Box>
                </Card>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                p: 2,
                borderTop: "1px solid #eee",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  mb: 1.2,
                  p: 1.2,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  border: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>Total Qty</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{num(qty)}</Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                disabled={submitDisabled}
                onClick={submitDamaged}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#00A4E2",
                  py: 1.1,
                  fontWeight: 900,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    backgroundColor: "#cfefff",
                    color: "#5b90a7",
                  },
                }}
              >
                {posting ? "Posting..." : "Submit Damaged Product"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DamagedProducts;
